import React, { useState, useEffect } from 'react';
import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { useProject } from '@/context/ProjectContext';
import { projectsAPI } from '@/lib/api';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Separator } from '@/components/ui/separator';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { GalaxyIcon } from '@/components/icons';
import { TrialBanner } from '@/components/TrialBanner';
import DemoBanner from '@/components/DemoBanner';
import { WalkthroughOverlay, USER_WALKTHROUGH_STEPS } from '@/pages/AdminOnboardingCarousel';
import { 
  FolderKanban, 
  Target, 
  Users, 
  FileQuestion,
  BarChart3,
  Settings,
  LogOut,
  Menu,
  X,
  ChevronDown,
  Activity,
  Layers,
  User,
  UsersRound,
  PieChart,
  Shield,
  Building2,
  ArrowLeft,
  Globe,
  Grid3x3,
  Crown,
  CreditCard,
  HelpCircle,
  LayoutDashboard,
  ShieldCheck
} from 'lucide-react';

const navItems = [
  { path: '/dashboard', label: 'Projects', icon: FolderKanban },
];

// User navigation items (visible to all users)
const userNavItems = [
  { path: '/about-me', label: 'About Me', icon: User },
  { path: '/about-others', label: 'About Others', icon: UsersRound },
];

// Dashboard navigation items (visible to all users)
const dashboardNavItems = [
  { path: '/dashboards/summary', label: 'Project Summary', icon: PieChart, pageId: 'project_summary' },
  { path: '/dashboards/driver-analysis', label: 'Driver Analysis', icon: Target, pageId: 'driver_analysis' },
  { path: '/dashboards/segment-analysis', label: 'Segment Analysis', icon: Layers, pageId: 'segment_analysis' },
  { path: '/dashboards/public-analysis', label: 'Public Analysis', icon: Globe, pageId: 'public_analysis' },
  { path: '/dashboards/galaxy', label: 'People Galaxy', icon: GalaxyIcon, pageId: 'people_galaxy' },
  { path: '/dashboards/matrix', label: 'Matrix', icon: Grid3x3, pageId: 'matrix_view' },
];

// Admin navigation items (visible only to admins)
const adminNavItems = [
  { path: '/admin-dashboard', label: 'Admin Dashboard', icon: LayoutDashboard },
  { path: '/drivers', label: 'Drivers', icon: Target },
  { path: '/segments', label: 'Segments', icon: Layers },
  { path: '/questions', label: 'Questions', icon: FileQuestion },
  { path: '/stakeholders', label: 'Stakeholders', icon: Users },
];

export const AppLayout = ({ children }) => {
  const { user, logout, isImpersonating, returnFromImpersonation } = useAuth();
  const { currentProject, projects, fetchProjects, selectProject, isProjectAdmin, isAccountOwner, userPermissions } = useProject();
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  
  // Completion gate modals
  const [aboutMeModalOpen, setAboutMeModalOpen] = useState(false);
  const [galaxyModalOpen, setGalaxyModalOpen] = useState(false);
  const [completionStatus, setCompletionStatus] = useState(null);
  const [showWalkthrough, setShowWalkthrough] = useState(false);
  const [showUserWalkthrough, setShowUserWalkthrough] = useState(false);
  const [showDashboardDisclaimer, setShowDashboardDisclaimer] = useState(false);

  // Listen for walkthrough event from onboarding carousel (admin)
  useEffect(() => {
    const handler = () => {
      if (window.innerWidth < 768) {
        setSidebarOpen(true);
      }
      setShowWalkthrough(true);
    };
    window.addEventListener('start-walkthrough', handler);
    return () => window.removeEventListener('start-walkthrough', handler);
  }, []);

  // Listen for user walkthrough event from non-admin onboarding carousel
  useEffect(() => {
    const handler = () => {
      if (window.innerWidth < 768) {
        setSidebarOpen(true);
      }
      setShowUserWalkthrough(true);
    };
    window.addEventListener('start-user-walkthrough', handler);
    return () => window.removeEventListener('start-user-walkthrough', handler);
  }, []);

  // Auto-trigger walkthrough for new non-admin users on first visit
  useEffect(() => {
    if (!user || !currentProject?.id) return;
    const isAdmin = user.role === 'owner' || user.is_global_admin;
    if (isAdmin) return;
    // Check if this user hasn't completed onboarding yet
    const walkthroughShown = sessionStorage.getItem('pulse_user_walkthrough_shown');
    if (!walkthroughShown && user.onboarding_completed === false) {
      const timer = setTimeout(() => {
        if (window.innerWidth < 768) {
          setSidebarOpen(true);
        }
        setShowUserWalkthrough(true);
        sessionStorage.setItem('pulse_user_walkthrough_shown', 'true');
      }, 1200);
      return () => clearTimeout(timer);
    }
  }, [user, currentProject]);

  // Check if user is a global admin
  const isGlobalAdmin = user?.is_global_admin === true;
  const impersonating = isImpersonating();

  useEffect(() => {
    fetchProjects();
  }, [fetchProjects]);

  // Fetch user completion status for gate checks
  useEffect(() => {
    if (!currentProject?.id) {
      setCompletionStatus(null);
      return;
    }
    const fetchStatus = async () => {
      try {
        const res = await api.get(`/projects/${currentProject.id}/user-completion-status`);
        setCompletionStatus(res.data);
      } catch {
        setCompletionStatus(null);
      }
    };
    fetchStatus();
  }, [currentProject?.id]);

  // Auto-select project from URL
  useEffect(() => {
    const pathParts = location.pathname.split('/');
    const projectIndex = pathParts.indexOf('project');
    if (projectIndex !== -1 && pathParts[projectIndex + 1]) {
      const projectId = pathParts[projectIndex + 1];
      if (!currentProject || currentProject.id !== projectId) {
        selectProject(projectId);
      }
    }
  }, [location.pathname, currentProject, selectProject]);

  // Show dashboard disclaimer on first visit to any dashboard page per project
  useEffect(() => {
    const isDashboardRoute = location.pathname.includes('/dashboards/') || location.pathname.includes('/my-dashboards');
    if (!isDashboardRoute || !user || !currentProject?.id) return;
    const key = `pulse_disclaimer_${user.id}_${currentProject.id}`;
    if (!localStorage.getItem(key)) {
      setShowDashboardDisclaimer(true);
    }
  }, [location.pathname, user, currentProject?.id]);

  const handleAcceptDisclaimer = () => {
    if (user && currentProject?.id) {
      localStorage.setItem(`pulse_disclaimer_${user.id}_${currentProject.id}`, 'accepted');
    }
    setShowDashboardDisclaimer(false);
  };

  const handleProjectChange = async (projectId) => {
    await selectProject(projectId);
    // Navigate to About Me for regular users, Drivers for admins
    navigate(`/project/${projectId}/about-me`);
  };

  const handleLogout = () => {
    logout();
    // Keep the last project ID in localStorage so user returns to it on next login
    navigate('/login');
  };

  // Check if user has admin access
  const hasAdminAccess = isProjectAdmin || isAccountOwner || isGlobalAdmin;

  const getInitials = (firstName, lastName) => {
    return `${firstName?.[0] || ''}${lastName?.[0] || ''}`.toUpperCase();
  };

  return (
    <div className="app-layout">
      {/* Impersonation Banner */}
      {impersonating && (
        <div className="fixed top-0 left-0 right-0 z-50 bg-amber-500 text-white px-4 py-2 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Shield className="h-4 w-4" />
            <span className="text-sm font-medium">
              Viewing as: {JSON.parse(localStorage.getItem('impersonating') || '{}').email}
            </span>
          </div>
          <Button 
            size="sm" 
            variant="secondary"
            onClick={returnFromImpersonation}
            className="gap-2"
          >
            <ArrowLeft className="h-4 w-4" />
            Return to Admin
          </Button>
        </div>
      )}

      {/* Mobile overlay */}
      <div 
        className={`sidebar-overlay ${sidebarOpen ? 'open' : ''}`}
        onClick={() => setSidebarOpen(false)}
      />

      {/* Sidebar */}
      <aside className={`sidebar ${sidebarOpen ? 'open' : ''}`} style={{ marginTop: impersonating ? '48px' : '0' }}>
        {/* Logo */}
        <div className="sidebar-header">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <img 
                src="/pulse-logo-with-name.png" 
                alt="Pulse by ProjectAI" 
                className="object-contain"
                style={{ height: '45px' }}
              />
            </div>
            <Button 
              variant="ghost" 
              size="icon"
              className="md:hidden"
              onClick={() => setSidebarOpen(false)}
            >
              <X className="h-5 w-5" />
            </Button>
          </div>
        </div>

        <ScrollArea className="flex-1">
          <nav className="sidebar-nav">
            {/* Global Admin Section */}
            {isGlobalAdmin && !impersonating && (
              <>
                <div className="px-1 mb-2 flex items-center gap-2">
                  <Shield className="h-3 w-3 text-amber-500" />
                  <label className="text-xs font-medium text-amber-500 uppercase tracking-wide">
                    Global Admin
                  </label>
                </div>
                <div className="space-y-1 mb-4">
                  <NavLink
                    to="/admin/customers"
                    className={({ isActive }) => `sidebar-item ${isActive ? 'active' : ''}`}
                    onClick={() => setSidebarOpen(false)}
                    data-testid="nav-customer-accounts"
                  >
                    <Building2 className="h-5 w-5" />
                    <span>Customer Accounts</span>
                  </NavLink>
                  <NavLink
                    to="/admin/data-analysis"
                    className={({ isActive }) => `sidebar-item ${isActive ? 'active' : ''}`}
                    onClick={() => setSidebarOpen(false)}
                    data-testid="nav-data-analysis"
                  >
                    <BarChart3 className="h-5 w-5" />
                    <span>Data Analysis</span>
                  </NavLink>
                  <NavLink
                    to="/admin/pricing"
                    className={({ isActive }) => `sidebar-item ${isActive ? 'active' : ''}`}
                    onClick={() => setSidebarOpen(false)}
                    data-testid="nav-pricing"
                  >
                    <CreditCard className="h-5 w-5" />
                    <span>Pricing & Plans</span>
                  </NavLink>
                </div>
                <Separator className="my-4" />
              </>
            )}

            {/* Main nav */}
            <div className="space-y-1">
              {navItems.map((item) => (
                <NavLink
                  key={item.path}
                  to={item.path}
                  className={({ isActive }) => `sidebar-item ${isActive ? 'active' : ''}`}
                  onClick={() => setSidebarOpen(false)}
                  data-testid={`nav-${item.label.toLowerCase()}`}
                >
                  <item.icon className="h-5 w-5" />
                  <span>{item.label}</span>
                </NavLink>
              ))}
            </div>

            {/* Project selector */}
            {projects.length > 0 && (
              <>
                <Separator className="my-4" />
                <div className="px-1 mb-2">
                  <label className="text-xs font-medium text-slate-400 uppercase tracking-wide">
                    Current Project
                  </label>
                </div>
                <Select 
                  value={currentProject?.id || ''} 
                  onValueChange={handleProjectChange}
                >
                  <SelectTrigger 
                    className="w-full mb-3"
                    data-testid="project-selector"
                  >
                    <SelectValue placeholder="Select a project" />
                  </SelectTrigger>
                  <SelectContent>
                    {projects.map((project) => (
                      <SelectItem key={project.id} value={project.id}>
                        <div className="flex items-center gap-2 w-full">
                          <span className="truncate flex-1">{project.name}</span>
                          {project.user_role === 'owner' && (
                            <Crown className="h-4 w-4 text-amber-500 flex-shrink-0" />
                          )}
                          {project.user_role === 'admin' && (
                            <Shield className="h-4 w-4 text-emerald-500 flex-shrink-0" />
                          )}
                          {!project.is_own_account && (
                            <span className="text-[10px] text-slate-400 flex-shrink-0">ext</span>
                          )}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                {/* User Section - visible to all */}
                {currentProject && (
                  <>
                    <div className="space-y-1">
                      {userNavItems.map((item) => (
                        <NavLink
                          key={item.path}
                          to={`/project/${currentProject.id}${item.path}`}
                          className={({ isActive }) => `sidebar-item ${isActive ? 'active' : ''}`}
                          onClick={() => setSidebarOpen(false)}
                          data-testid={`nav-user-${item.label.toLowerCase().replace(' ', '-')}`}
                        >
                          <item.icon className="h-5 w-5" />
                          <span>{item.label}</span>
                        </NavLink>
                      ))}
                    </div>

                    {/* Dashboards Section */}
                    <Separator className="my-4" />
                    <div className="px-1 mb-2 flex items-center gap-2" data-testid="nav-dashboards-group">
                      <PieChart className="h-3 w-3 text-slate-400" />
                      <label className="text-xs font-medium text-slate-400 uppercase tracking-wide">
                        Dashboards
                      </label>
                    </div>
                    <div className="space-y-1">
                      {dashboardNavItems
                        .filter(item => {
                          // If user has page restrictions, only show allowed pages
                          if (userPermissions?.allowed_pages !== null && userPermissions?.allowed_pages !== undefined) {
                            return userPermissions.allowed_pages.includes(item.pageId);
                          }
                          return true;
                        })
                        .map((item) => {
                        const isGalaxy = item.path === '/dashboards/galaxy';
                        const isDashboard = !isGalaxy;
                        
                        // Check if gate should block
                        const shouldBlockDashboard = isDashboard && completionStatus && !completionStatus.dashboards_unlocked;
                        const shouldBlockGalaxy = isGalaxy && completionStatus && !completionStatus.galaxy_unlocked;
                        const isBlocked = shouldBlockDashboard || shouldBlockGalaxy;
                        
                        if (isBlocked) {
                          return (
                            <button
                              key={item.path}
                              className="sidebar-item w-full text-left opacity-70"
                              onClick={() => {
                                setSidebarOpen(false);
                                if (shouldBlockGalaxy) setGalaxyModalOpen(true);
                                else setAboutMeModalOpen(true);
                              }}
                              data-testid={`nav-dashboard-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                            >
                              <item.icon className="h-5 w-5" />
                              <span>{item.label}</span>
                            </button>
                          );
                        }
                        
                        return (
                          <NavLink
                            key={item.path}
                            to={`/project/${currentProject.id}${item.path}`}
                            className={({ isActive }) => `sidebar-item ${isActive ? 'active' : ''}`}
                            onClick={() => setSidebarOpen(false)}
                            data-testid={`nav-dashboard-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                          >
                            <item.icon className="h-5 w-5" />
                            <span>{item.label}</span>
                          </NavLink>
                        );
                      })}
                    </div>

                    {/* Administration Section - visible only to admins */}
                    {hasAdminAccess && (
                      <>
                        <Separator className="my-4" />
                        <div className="px-1 mb-2 flex items-center gap-2" data-testid="nav-admin-group">
                          <Shield className="h-3 w-3 text-slate-400" />
                          <label className="text-xs font-medium text-slate-400 uppercase tracking-wide">
                            Administration
                          </label>
                        </div>
                        <div className="space-y-1">
                          {adminNavItems.map((item) => (
                            <NavLink
                              key={item.path}
                              to={`/project/${currentProject.id}${item.path}`}
                              className={({ isActive }) => `sidebar-item ${isActive ? 'active' : ''}`}
                              onClick={() => setSidebarOpen(false)}
                              data-testid={`nav-admin-${item.label.toLowerCase().replace(' ', '-')}`}
                            >
                              <item.icon className="h-5 w-5" />
                              <span>{item.label}</span>
                            </NavLink>
                          ))}
                        </div>
                      </>
                    )}
                  </>
                )}
              </>
            )}
          </nav>
        </ScrollArea>

        {/* User section */}
        <div className="p-4 border-t border-slate-200">
          {/* Help Page Link */}
          <NavLink 
            to="/help" 
            className={({ isActive }) => `flex items-center gap-2 mb-3 px-2 py-2 rounded-lg transition-colors cursor-pointer ${
              isActive 
                ? 'bg-[#5AA2AE]/10 text-[#5AA2AE]' 
                : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900'
            }`}
            data-testid="help-page-link"
          >
            <HelpCircle className="h-4 w-4" />
            <span className="text-sm font-medium">Help & Support</span>
          </NavLink>
          
          {/* Manage Account - only for account owners (check user role or project-level ownership) */}
          {(isAccountOwner || user?.role === 'owner') && (
            <NavLink 
              to="/manage-account" 
              className="flex items-center gap-2 mb-3 px-2 py-2 rounded-lg hover:bg-slate-100 transition-colors cursor-pointer text-slate-600 hover:text-slate-900"
              data-testid="manage-account-link"
            >
              <Settings className="h-4 w-4" />
              <span className="text-sm font-medium">Manage Account</span>
            </NavLink>
          )}
          <NavLink 
            to="/profile" 
            className="flex items-center gap-3 mb-3 p-2 -m-2 rounded-lg hover:bg-slate-100 transition-colors cursor-pointer"
            data-testid="user-profile-link"
          >
            <Avatar className="h-9 w-9">
              <AvatarFallback className="bg-[#5AA2AE] text-white text-sm">
                {getInitials(user?.first_name, user?.last_name)}
              </AvatarFallback>
            </Avatar>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-slate-900 truncate">
                {user?.first_name} {user?.last_name}
              </p>
              <p className="text-xs text-slate-500 truncate">{user?.email}</p>
            </div>
          </NavLink>
          <Button 
            variant="ghost" 
            className="w-full justify-start text-slate-600 hover:text-slate-900"
            onClick={handleLogout}
            data-testid="logout-btn"
          >
            <LogOut className="h-4 w-4 mr-2" />
            Sign out
          </Button>
        </div>
      </aside>

      {/* Main content */}
      <main className="main-content" style={{ marginTop: impersonating ? '48px' : '0' }}>
        {/* Trial Reminder Banner - Account Owners only */}
        {!impersonating && <TrialBanner />}
        {/* Demo Project Banner */}
        {currentProject?.is_demo_project && <DemoBanner />}

        {/* Mobile header */}
        <header className="md:hidden sticky top-0 z-20 bg-white border-b border-slate-200 px-4 py-3 flex items-center gap-3">
          <Button 
            variant="ghost" 
            size="icon"
            onClick={() => setSidebarOpen(true)}
            data-testid="mobile-menu-btn"
          >
            <Menu className="h-5 w-5" />
          </Button>
          <div className="flex items-center gap-2">
            <img src="/pulse-logo-with-name.png" alt="Pulse" className="h-9 object-contain" />
          </div>
        </header>

        {/* Page content */}
        <div className="p-6 md:p-8">
          {children}
        </div>
      </main>
      {/* About Me Completion Modal */}
      <Dialog open={aboutMeModalOpen} onOpenChange={setAboutMeModalOpen}>
        <DialogContent className="sm:max-w-md" data-testid="about-me-gate-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-lg">
              <User className="h-5 w-5 text-[#5AA2AE]" />
              Complete Your Profile First
            </DialogTitle>
            <DialogDescription className="text-sm text-slate-600 pt-2">
              Before diving into the dashboards, we'd love to learn a bit more about you! Please complete the questions in your <strong>About Me</strong> section. This helps us personalize your experience and provide meaningful insights.
            </DialogDescription>
          </DialogHeader>
          {completionStatus && (
            <div className="py-3">
              <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
                <div className="flex-1">
                  <p className="text-sm font-medium text-slate-700">Your progress</p>
                  <p className="text-xs text-slate-500">{completionStatus.about_me_answered} of {completionStatus.about_me_total} questions answered</p>
                </div>
                <div className="w-16 h-16 relative">
                  <svg className="w-16 h-16 -rotate-90" viewBox="0 0 64 64">
                    <circle cx="32" cy="32" r="28" fill="none" stroke="#e2e8f0" strokeWidth="4" />
                    <circle cx="32" cy="32" r="28" fill="none" stroke="#5AA2AE" strokeWidth="4"
                      strokeDasharray={`${completionStatus.about_me_total > 0 ? (completionStatus.about_me_answered / completionStatus.about_me_total) * 175.9 : 0} 175.9`}
                      strokeLinecap="round" />
                  </svg>
                  <span className="absolute inset-0 flex items-center justify-center text-xs font-bold text-slate-700">
                    {completionStatus.about_me_total > 0 ? Math.round((completionStatus.about_me_answered / completionStatus.about_me_total) * 100) : 0}%
                  </span>
                </div>
              </div>
            </div>
          )}
          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="outline" onClick={() => setAboutMeModalOpen(false)}>
              Maybe Later
            </Button>
            <Button 
              className="bg-[#5AA2AE] hover:bg-[#4a8a94]"
              onClick={() => {
                setAboutMeModalOpen(false);
                if (currentProject) navigate(`/project/${currentProject.id}/about-me`);
              }}
              data-testid="go-to-about-me-btn"
            >
              Go to About Me
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Galaxy Minimum Mappings Modal */}
      <Dialog open={galaxyModalOpen} onOpenChange={setGalaxyModalOpen}>
        <DialogContent className="sm:max-w-md" data-testid="galaxy-gate-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-lg">
              <UsersRound className="h-5 w-5 text-[#5AA2AE]" />
              Map More Stakeholders
            </DialogTitle>
            <DialogDescription className="text-sm text-slate-600 pt-2">
              The People Galaxy comes to life when you've mapped enough stakeholders. Add a few more people in your <strong>About Others</strong> section to unlock this view and see how your network connects!
            </DialogDescription>
          </DialogHeader>
          {completionStatus && (
            <div className="py-3">
              <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
                <div className="flex-1">
                  <p className="text-sm font-medium text-slate-700">Your mappings</p>
                  <p className="text-xs text-slate-500">
                    {completionStatus.mapping_count} of {completionStatus.min_galaxy_mappings} required mappings
                  </p>
                </div>
                <div className="flex items-center gap-1">
                  {Array.from({ length: completionStatus.min_galaxy_mappings }, (_, i) => (
                    <div key={i} className={`w-3 h-3 rounded-full ${i < completionStatus.mapping_count ? 'bg-[#5AA2AE]' : 'bg-slate-200'}`} />
                  ))}
                </div>
              </div>
            </div>
          )}
          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="outline" onClick={() => setGalaxyModalOpen(false)}>
              Maybe Later
            </Button>
            <Button 
              className="bg-[#5AA2AE] hover:bg-[#4a8a94]"
              onClick={() => {
                setGalaxyModalOpen(false);
                if (currentProject) navigate(`/project/${currentProject.id}/about-others`);
              }}
              data-testid="go-to-about-others-btn"
            >
              Go to About Others
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Walkthrough overlay from onboarding carousel (admin) */}
      {showWalkthrough && <WalkthroughOverlay onComplete={() => {
        setShowWalkthrough(false);
        if (window.innerWidth < 768) {
          setSidebarOpen(false);
        }
      }} />}

      {/* User walkthrough overlay (non-admin) */}
      {showUserWalkthrough && <WalkthroughOverlay steps={USER_WALKTHROUGH_STEPS} onComplete={() => {
        setShowUserWalkthrough(false);
        if (window.innerWidth < 768) {
          setSidebarOpen(false);
        }
        // Mark onboarding as completed so it doesn't fire again
        const token = localStorage.getItem('pulse_token');
        if (token) {
          fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/complete-onboarding`, {
            method: 'POST',
            headers: { 'Authorization': `Bearer ${token}` },
          }).catch(() => {});
        }
      }} />}

      {/* Dashboard Disclaimer Modal */}
      <Dialog open={showDashboardDisclaimer} onOpenChange={setShowDashboardDisclaimer}>
        <DialogContent className="sm:max-w-lg" data-testid="dashboard-disclaimer-modal">
          <DialogHeader>
            <div className="flex items-center gap-3 mb-1">
              <div className="w-10 h-10 rounded-full bg-[#5AA2AE]/10 flex items-center justify-center">
                <ShieldCheck className="w-5 h-5 text-[#5AA2AE]" />
              </div>
              <DialogTitle className="text-lg font-semibold">Important Information</DialogTitle>
            </div>
            <DialogDescription className="sr-only">Dashboard data disclaimer and liability notice</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 text-sm text-slate-600 leading-relaxed">
            <p>
              This report was compiled using data provided by administrators and respondents using <strong>Pulse</strong> by ProjectAI. Artificial intelligence has been used to aggregate and anonymise the insights presented.
            </p>
            <p>
              The analysis should be viewed as broadly indicative of the feedback received and is intended to support — not replace — professional judgement.
            </p>
            <p className="text-slate-500 text-xs">
              Dashboard content may be filtered based on your specific permissions. As a result, the metrics and insights reported may differ between users.
            </p>
            <p className="text-slate-500 text-xs border-t pt-3 mt-3">
              ProjectAI Asia-Pacific Pty Ltd and its associated companies accept no liability for any decisions or actions taken by any person as a result of the use of Pulse. All customers and users indemnify ProjectAI Asia-Pacific Pty Ltd and its associated companies from any loss resulting from the use of the software.
            </p>
          </div>
          <DialogFooter>
            <Button
              onClick={handleAcceptDisclaimer}
              className="w-full bg-[#5AA2AE] hover:bg-[#4a8a94]"
              data-testid="disclaimer-accept-btn"
            >
              I Understand
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AppLayout;
